/**
 * ProductDetail.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCart } from '../hooks/useCart';
import { useAuth } from '../context/AuthContext';
import { useToast } from './ToastContainer';
import { getWholesalePrice, getAvailableWholesaleTiers } from '../data/products';
import { getIcon } from '../utils/icons';
import AsyncProductImage from './AsyncProductImage';
import ShareModal from './ShareModal';
import UserContentFeedback from './UserContentFeedback';
import SEO from './SEO';
import LoadingSpinner from './LoadingSpinner';
import { trackProductView } from '../utils/analytics';
import ShareFloatingButton from './ShareFloatingButton';
import TuneFormatMarker from './TuneFormatMarker';
import { getVariantAdjustedPriceCents, getVariantPriceAdjustmentCents } from '../utils/variantPricing';
import { buildBundleTaxAllocations } from '../utils/bundleTax';
import { useLanguage } from '../context/LanguageContext';
import { fmt } from '../i18n/format';
import './ProductCard.css';
import './ProductDetail.css';

const PRODUCTS_URL = '/api/products';

/** One POST (200) instead of many GET /pages/:id probes that 404 and clutter the console. */
async function bulkLookupPages(pageIds) {
  const unique = [...new Set(pageIds.filter(Boolean))];
  if (unique.length === 0) return [];
  try {
    const res = await fetch('/api/content/lookup/bulk-pages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: unique })
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.pages) ? data.pages : [];
  } catch {
    return [];
  }
}

/**
 * Links from `products.source_url`. Primary link always uses the exact stored URL as `href`.
 * For legacy www.cranfordpub.com / cranfordpub.com product pages, adds an optional static mirror on old.cranfordpub.ca (same path).
 */
function getProductSourceUrlPairs(sourceUrl) {
  if (!sourceUrl || typeof sourceUrl !== 'string') return [];
  const trimmed = sourceUrl.trim();
  if (!trimmed) return [];
  let u;
  try {
    u = new URL(trimmed);
  } catch {
    const href = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed.replace(/^\/+/, '')}`;
    return [{ label: trimmed.replace(/^https?:\/\//i, ''), href }];
  }
  const host = (u.hostname || '').toLowerCase();
  const path = `${u.pathname || '/'}${u.search || ''}${u.hash || ''}`;
  const primaryLabel =
    (u.host.replace(/^www\./i, '') + (path === '/' ? '' : path)) || u.href.replace(/^https?:\/\//i, '');
  const pairs = [{ label: primaryLabel, href: u.href }];

  const legacyCranfordHosts = new Set(['www.cranfordpub.com', 'cranfordpub.com']);
  if (legacyCranfordHosts.has(host) && path && path !== '/') {
    pairs.push({
      label: 'old.cranfordpub.ca (archive)',
      href: `https://old.cranfordpub.ca${path}`
    });
  }
  return pairs;
}

function ProductOriginalPageLinks({ sourceUrl }) {
  const pairs = getProductSourceUrlPairs(sourceUrl);
  if (!pairs.length) return null;
  return (
    <div
      style={{
        marginBottom: '1rem',
        padding: '0.75rem',
        background: 'rgba(74, 144, 226, 0.1)',
        borderRadius: '6px',
        border: '1px solid rgba(74, 144, 226, 0.3)'
      }}
    >
      <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary, #999)', marginBottom: '0.35rem' }}>
        Original page
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem 1rem', alignItems: 'center', fontSize: '0.9rem' }}>
        {pairs.map(({ label, href }) => (
          <a
            key={href}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#4a90e2',
              textDecoration: 'none',
              fontWeight: 500
            }}
          >
            {label}
          </a>
        ))}
      </div>
    </div>
  );
}

const ProductDetail = () => {
  const { id } = useParams();
  const { currentUser, getUserWholesaleTier, hasAnyRole } = useAuth();
  const { showSuccess, showError } = useToast();
  const { t } = useLanguage();
  const p = t.shop.product;
  const b = t.shop.bundle;
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [relatedTunes, setRelatedTunes] = useState([]);
  const [tunesLoading, setTunesLoading] = useState(false);
  const [indexPageExists, setIndexPageExists] = useState(false);
  const [indexPageId, setIndexPageId] = useState(null);
  const [reviewPageExists, setReviewPageExists] = useState(false);
  const [reviewPageId, setReviewPageId] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [relatedProductsLoading, setRelatedProductsLoading] = useState(false);
  const [addingRelatedProduct, setAddingRelatedProduct] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [bundleProducts, setBundleProducts] = useState([]);
  const [selectedBundleProducts, setSelectedBundleProducts] = useState([]);
  const [fixedBundleOptions, setFixedBundleOptions] = useState([]);
  const [activeBundleOption, setActiveBundleOption] = useState(null);
  // State to track variant selections for each bundle product
  // Format: { productId: { size: 'M', color: 'Black', sizes: [...], colors: [...] }, ... }
  const [bundleVariantSelections, setBundleVariantSelections] = useState({});

  // Variant state
  const [hasVariants, setHasVariants] = useState(false);
  const [, setVariantsData] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [availableSizes, setAvailableSizes] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  const [digitalAlternative, setDigitalAlternative] = useState(null);
  const { addItem, refreshProducts } = useCart();
  const navigate = useNavigate();

  const isWholesaler = currentUser?.isWholesaler;
  const wholesaleTier = getUserWholesaleTier();

  const canEdit =
    Boolean(currentUser && hasAnyRole && hasAnyRole(['admin', 'super_admin']));

  useEffect(() => {
    setLoading(true);
    fetch(`${PRODUCTS_URL}/${id}`)
      .then(res => {
        if (!res.ok) {
          throw new Error('Product not found');
        }
        return res.json();
      })
      .then(productData => {
        setProduct(productData);
        setLoading(false);
        refreshProducts();

        // Canonical product URL uses permalink — redirect numeric ID URLs
        const permalink = productData.permalink && String(productData.permalink).trim();
        if (permalink && String(id) !== permalink && /^\d+$/.test(String(id))) {
          navigate(`/product/${permalink}`, { replace: true });
        }

        // Parse variants
        if (productData.has_variants) {
          try {
            const vData = typeof productData.variants_data === 'string'
              ? JSON.parse(productData.variants_data)
              : productData.variants_data;

            if (vData) {
              setHasVariants(true);
              setVariantsData(vData);
              setAvailableSizes(vData.sizes || []);
              setAvailableColors(vData.colors || []);

              // Auto-select if only one option
              if (vData.sizes && vData.sizes.length === 1) setSelectedSize(vData.sizes[0]);
              if (vData.colors && vData.colors.length === 1) setSelectedColor(vData.colors[0].name);
            }
          } catch (e) {
            console.error('Error parsing variants:', e);
          }
        }

        // Parse bundle options
        if (productData.bundle_options) {
          try {
            const bOptions = typeof productData.bundle_options === 'string'
              ? JSON.parse(productData.bundle_options)
              : productData.bundle_options;
            setFixedBundleOptions(bOptions || []);
          } catch (e) {
            console.error('Error parsing bundle options:', e);
            setFixedBundleOptions([]);
          }
        }

        // Track product view in Google Analytics
        trackProductView(productData);

        if (productData.related_digital_product_id) {
          fetch(`${PRODUCTS_URL}/${productData.related_digital_product_id}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((digital) => {
              if (digital && (digital.is_digital_album === 1 || digital.is_digital_album === true)) {
                setDigitalAlternative(digital);
              } else {
                setDigitalAlternative(null);
              }
            })
            .catch(() => setDigitalAlternative(null));
        } else {
          setDigitalAlternative(null);
        }

        // Fetch related tunes if this is a book or CD (especially for product 686 - Cape Breton Fiddle Compilation)
        const isBook = productData && (productData.category?.toLowerCase().includes('book') || productData.item_type?.toLowerCase() === 'book');
        const isCD = productData && (productData.category?.toLowerCase().includes('cd') || productData.item_type?.toLowerCase() === 'cd');
        const isDigitalAlbum = productData && (productData.is_digital_album === 1 || productData.is_digital_album === true);
        const shouldFetchTunes = isBook || isCD || isDigitalAlbum || id === '686'; // Also fetch for product 686 specifically

        if (productData && shouldFetchTunes) {
          setTunesLoading(true);
          fetch(`/api/content/products/${id}/tunes`)
            .then(res => res.ok ? res.json() : [])
            .then(tunes => {
              setRelatedTunes(tunes);
              setTunesLoading(false);
            })
            .catch(err => {
              console.error('Error fetching related tunes:', err);
              setTunesLoading(false);
            });

          // Check if index page exists for this book (try multiple patterns)
          const checkIndexPage = async () => {
            const normalizeIndexSlug = (raw) => {
              const s = String(raw || '').trim();
              if (!s) return '';
              const m = s.match(/\/page\/([^/?#]+)/i);
              if (m) return m[1].trim();
              return s.replace(/^\/+/, '');
            };

            const explicitFromProduct = normalizeIndexSlug(productData.index_page_id);

            if (!productData.sku && !explicitFromProduct) {
              setIndexPageExists(false);
              setIndexPageId(null);
              return;
            }

            // Generate possible index page ID patterns from product data
            const productName = productData.name || '';
            const productSku = productData.sku || '';

            // Extract key words from product name (e.g., "Winston Fitzgerald's Collection" -> "winston-fitzgerald")
            const nameWords = productName.toLowerCase()
              .replace(/['"]/g, '')
              .split(/\s+/)
              .filter(w => w.length > 2 && !['the', 'and', 'or', 'collection', 'book'].includes(w))
              .slice(0, 3)  // Take first 3 significant words
              .join('-');

            // Try multiple possible index page ID patterns
            const possibleIds = [
              ...(explicitFromProduct ? [explicitFromProduct] : []),
              // Explicit index page IDs for known products (check first)
              id === '625' ? 'the-cape-breton-fidd-bk-index' : null,  // The Cape Breton Fiddler
              id === '639' ? 'winston-fitzgerald-index' : null,
              id === '640' ? 'stubbert2nd-index' : null,  // Brenda Stubbert 2nd Collection
              id === '715' ? 'stubbert3rd-index' : null,  // Brenda Stubbert: The 3rd Collection
              id === '641' ? 'skye-index' : null,  // The Skye Collection
              id === '653' ? 'the-gathering-of-the-bk-index' : null,  // The Gathering of the Clans Collection V. 1
              id === '698' ? 'cc-cd-index' : null,  // Classic Cuts - Winston Fitzgerald
              id === '690' ? 'bdalh-cd-index' : null,  // Donald Angus Beaton: Live at the House
              id === '691' ? 'bmcn-bs-cd-index' : null,  // The Bright Side
              id === '692' ? 'maci-lscd-index' : null,  // Live at the Savoy
              id === '693' ? 'ba-h-cd-index' : null,  // Hindsight
              id === '694' ? 'gd-mvo-cd-index' : null,  // Multiple Voice for One
              id === '697' ? 'product-697-index' : null,  // For Each New Day
              id === '699' ? 'bka-cd-index' : null,  // Kinnon and Andrea Beaton
              id === '700' ? 'bkt-cd-index' : null,  // Kinnon Beaton - Tunesmith
              id === '724' ? 'jh-solo-mp3-index' : null,  // Jerry Holland Solo (Digital/mp3)
              id === '667' ? 'swjh-cd-index' : null,  // A Session with Jerry Holland
            id === '701' ? 'cpfl-cd-index' : null,  // The Lighthouse CD
              id === '702' ? 'cp-mtl-cd-index' : null,  // More Tunes from The Lighthouse
              `product-${id}-index`,  // Generic product ID-based
              `${productSku.toLowerCase()}-index`,  // SKU-based: wfc-b-index
              `${nameWords}-index`,  // Name-based: winston-fitzgerald-index
              `${productName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-index`,  // Full name-based
              `${productSku.toLowerCase().replace(/[^a-z0-9]+/g, '')}-index`,  // SKU without hyphens
              id === '643' ? 'celtic-colours-index' : null,  // Celtic Colours Collection
              id === '644' ? 'heather-hill-index' : null,  // Heather Hill Collection
              id === '645' ? 'trip-to-windsor-index' : null,  // Trip to Windsor Collection
            ].filter(id => id !== null);

            // Remove duplicates
            const uniqueIds = [...new Set(possibleIds.filter(pid => pid && pid.includes('index')))];

            const pages = await bulkLookupPages(uniqueIds);
            const found = new Set(pages.map((p) => p.pageId));
            for (const testPageId of uniqueIds) {
              if (found.has(testPageId)) {
                setIndexPageExists(true);
                setIndexPageId(testPageId);
                return;
              }
            }

            setIndexPageExists(false);
            setIndexPageId(null);
          };

          checkIndexPage();

          // Check if review page exists for this book (try multiple patterns)
          const checkReviewPage = async () => {
            if (!productData.sku) {
              setReviewPageExists(false);
              setReviewPageId(null);
              return;
            }

            const productName = productData.name || '';
            const productSku = productData.sku || '';

            const nameWords = productName.toLowerCase()
              .replace(/['"]/g, '')
              .split(/\s+/)
              .filter(w => w.length > 2 && !['the', 'and', 'or', 'collection', 'book'].includes(w))
              .slice(0, 3)
              .join('-');

            // Try multiple possible review page ID patterns
            const possibleIds = [
              `${productSku.toLowerCase()}-review`,
              `${nameWords}-review`,
              `${productName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-review`,
              `${productSku.toLowerCase().replace(/[^a-z0-9]+/g, '')}-review`,
              // Explicit review page IDs for known products
              id === '639' ? 'winston-fitzgerald-review' : null,
              id === '640' ? 'stubbert2nd-review' : null,
            ].filter(id => id !== null);

            const uniqueIds = [...new Set(possibleIds.filter(pid => pid && pid.includes('review')))];

            const pages = await bulkLookupPages(uniqueIds);
            const byId = new Map(pages.map((p) => [p.pageId, p]));

            for (const testPageId of uniqueIds) {
              const pageData = byId.get(testPageId);
              if (!pageData || pageData.pageType !== 'article-review') continue;
              try {
                const metadata = typeof pageData.metadata === 'string'
                  ? JSON.parse(pageData.metadata || '{}')
                  : (pageData.metadata || {});
                const reviewProductId = metadata.related_product_id || metadata.product_id;
                if (String(reviewProductId) === String(id)) {
                  setReviewPageExists(true);
                  setReviewPageId(testPageId);
                  return;
                }
              } catch (e) {
                setReviewPageExists(true);
                setReviewPageId(testPageId);
                return;
              }
            }

            setReviewPageExists(false);
            setReviewPageId(null);
          };

          checkReviewPage();
        }

        // Fetch related products based on tags
        setRelatedProductsLoading(true);
        fetch(`/api/products/${id}/related?limit=6`)
          .then(res => res.ok ? res.json() : [])
          .then(products => {
            setRelatedProducts(products);
            setRelatedProductsLoading(false);
          })
          .catch(err => {
            console.error('Error fetching related products:', err);
            setRelatedProductsLoading(false);
          });
      })
      .catch(error => {
        console.error('Error fetching product:', error);
        setProduct(null);
        setLoading(false);
      });
  }, [id]);

  // Fetch bundle products if this is a bundle product
  useEffect(() => {
    if (product) {
      console.log('📦 Product loaded:', {
        id: product.id,
        name: product.name,
        is_bundle: product.is_bundle,
        bundle_products: product.bundle_products,
        is_bundle_type: typeof product.is_bundle
      });

      const isBundle = product.is_bundle === 1 || product.is_bundle === true;

      if (isBundle && product.bundle_products) {
        console.log('📦 This is a bundle! Fetching bundle products...');
        const productIds = product.bundle_products.split(',').filter(id => id.trim());
        console.log('📦 Product IDs to fetch:', productIds);

        // Fetch all bundle products
        Promise.all(
          productIds.map(productId =>
            fetch(`${PRODUCTS_URL}/${productId.trim()}`)
              .then(res => res.ok ? res.json() : null)
              .catch(err => {
                console.error(`Error fetching bundle product ${productId}:`, err);
                return null;
              })
          )
        ).then(products => {
          const validProducts = products.filter(p => p !== null);
          console.log('📦 Fetched bundle products:', validProducts.length);
          setBundleProducts(validProducts);
        });
      } else {
        console.log('📦 Not a bundle product or no bundle_products defined');
        setBundleProducts([]);
        setSelectedBundleProducts([]);
      }
    }
  }, [product]);

  const handleQuantityChange = (e) => {
    setSelectedQuantity(parseInt(e.target.value));
  };

  const handleBundleOptionSelect = (option) => {
    if (activeBundleOption === option) {
      setActiveBundleOption(null);
      setSelectedBundleProducts([]);
      return;
    }

    setActiveBundleOption(option);

    // Parse option items to integers and filter valid products
    const optionProductIds = (option.items || [])
      .map(id => parseInt(id, 10))
      .filter(id => !isNaN(id));

    setSelectedBundleProducts(optionProductIds);
  };

  const bundleProductShortLabel = (item) => {
    if (!item) return '';
    if (item.is_digital_album === 1 || item.is_digital_album === true) return b.digitalMp3;
    const cat = (item.category || item.type || '').toLowerCase();
    if (cat.includes('book')) return b.tuneBook;
    if (cat.includes('audio') || cat.includes('cd')) return b.cd;
    const name = item.name || '';
    return name.length > 32 ? `${name.slice(0, 30)}…` : name;
  };

  const getOptionIncludedProducts = (option) => (
    (option?.items || [])
      .map((itemId) => {
        const pid = parseInt(String(itemId), 10);
        return bundleProducts.find((p) => p.id === pid);
      })
      .filter(Boolean)
  );

  const getBundleProductPricing = (bundleProduct) => {
    const variantData = bundleVariantSelections[bundleProduct.id];
    const selectedSize = variantData?.size || null;
    const selectedColor = variantData?.color || null;
    const adjustedPriceCents = getVariantAdjustedPriceCents(bundleProduct, selectedSize, selectedColor);
    const adjustmentCents = getVariantPriceAdjustmentCents(bundleProduct, selectedSize, selectedColor);
    return { adjustedPriceCents, adjustmentCents };
  };

  const formatAdjustmentLabel = (adjustmentCents) => {
    if (!adjustmentCents) return null;
    return `${adjustmentCents > 0 ? '+' : '-'}$${(Math.abs(adjustmentCents) / 100).toFixed(2)}`;
  };

  const areBundleVariantsCompleteFor = (productIds) => {
    for (const productId of productIds) {
      const variantData = bundleVariantSelections[productId];
      if (variantData) {
        if (variantData.sizes?.length > 0 && !variantData.size) return false;
        if (variantData.colors?.length > 0 && !variantData.color) return false;
      }
    }
    return true;
  };

  const getVariableBundlePricing = () => {
    const selectedProducts = bundleProducts.filter((p) => selectedBundleProducts.includes(p.id));
    const subtotalCents = selectedProducts.reduce((sum, p) => {
      const { adjustedPriceCents } = getBundleProductPricing(p);
      return sum + adjustedPriceCents;
    }, 0);
    const minQty = product?.bundle_min_quantity;
    const discountPct = product?.bundle_discount || 0;
    const qualifies = Boolean(minQty && selectedBundleProducts.length >= minQty);
    const discountAmountCents = qualifies ? Math.round(subtotalCents * (discountPct / 100)) : 0;
    const finalTotalCents = subtotalCents - discountAmountCents;
    const neededForDiscount = minQty && selectedBundleProducts.length < minQty
      ? minQty - selectedBundleProducts.length
      : 0;
    return { subtotalCents, discountAmountCents, finalTotalCents, qualifies, neededForDiscount, discountPct };
  };

  const getVariableBundleSelectionStatus = () => {
    const min = product?.bundle_min_quantity || 0;
    const discountPct = product?.bundle_discount || 0;
    const selected = selectedBundleProducts.length;
    const { neededForDiscount, qualifies } = getVariableBundlePricing();

    if (!min || !discountPct) {
      return {
        type: 'idle',
        message: selected
          ? fmt(selected === 1 ? b.itemsSelectedOne : b.itemsSelectedMany, { n: selected })
          : b.selectToBuild,
        progressPct: 0,
      };
    }
    if (qualifies) {
      return {
        type: 'success',
        message: fmt(b.discountUnlocked, { pct: discountPct }),
        progressPct: 100,
      };
    }
    if (selected === 0) {
      return {
        type: 'idle',
        message: fmt(b.pickMinToUnlock, { min, pct: discountPct }),
        progressPct: 0,
      };
    }
    return {
      type: 'progress',
      message: fmt(neededForDiscount === 1 ? b.pickMoreOne : b.pickMoreMany, {
        n: neededForDiscount,
        pct: discountPct,
      }),
      progressPct: Math.min(100, Math.round((selected / min) * 100)),
    };
  };

  const addBundleToCartInternal = (option, productIds) => {
    if (isAdding) return;

    const ids = productIds || selectedBundleProducts;
    if (!ids.length) {
      showError(b.errPickOne);
      return;
    }

    if (!option && product.bundle_min_quantity && product.bundle_discount && ids.length < product.bundle_min_quantity) {
      showError(fmt(b.errMinDiscount, { n: product.bundle_min_quantity }));
      return;
    }

    if (!areBundleVariantsCompleteFor(ids)) {
      showError(b.errVariants);
      return;
    }

    setIsAdding(true);

    try {
      const selectedProducts = bundleProducts
        .filter((p) => ids.includes(p.id))
        .map((p) => {
          const variantData = bundleVariantSelections[p.id];
          const selectedSize = variantData?.size || null;
          const selectedColor = variantData?.color || null;
          const variantPriceAdjustmentCents = getVariantPriceAdjustmentCents(p, selectedSize, selectedColor);
          const adjustedPriceCents = getVariantAdjustedPriceCents(p, selectedSize, selectedColor);

          if (variantData) {
            return {
              ...p,
              price_cents: adjustedPriceCents,
              price: adjustedPriceCents,
              selectedSize,
              selectedColor,
              variantPriceAdjustmentCents,
              cartItemId: `${p.id}-${variantData.size || ''}-${variantData.color || ''}`,
            };
          }
          return {
            ...p,
            price_cents: adjustedPriceCents,
            price: adjustedPriceCents,
            variantPriceAdjustmentCents,
          };
        });
      const bundleTotal = selectedProducts.reduce((sum, p) => sum + (p.price_cents || p.price), 0);

      let finalPrice = bundleTotal;
      let discountPercent = 0;

      if (option) {
        finalPrice = Math.round(parseFloat(option.price) * 100);
        const discountAmount = bundleTotal - finalPrice;
        discountPercent = bundleTotal > 0 ? Math.round((discountAmount / bundleTotal) * 100) : 0;
      } else {
        const discount = (product.bundle_min_quantity && ids.length >= product.bundle_min_quantity)
          ? product.bundle_discount || 0
          : 0;
        finalPrice = Math.round(bundleTotal * (1 - discount / 100));
        discountPercent = discount;
      }

      const bundleItem = {
        ...product,
        quantity: 1,
        unitPrice: finalPrice,
        originalPrice: bundleTotal,
        bundleDiscount: discountPercent,
        bundleOptionName: option ? option.name : null,
        bundleTaxAllocations:
          option?.taxAllocations || buildBundleTaxAllocations(selectedProducts, finalPrice),
        bundleProducts: selectedProducts,
        isBundle: true,
      };

      if (addItem(bundleItem, 1)) {
        showSuccess(`✓ ${fmt(b.addedNamed, { name: option?.name || product.name, price: (finalPrice / 100).toFixed(2) })}`);
        setSelectedBundleProducts([]);
        setActiveBundleOption(null);
        setBundleVariantSelections({});
      } else {
        showError(b.errAddFailed);
      }
    } catch (error) {
      showError(b.errAddFailed);
    }

    setTimeout(() => setIsAdding(false), 1000);
  };

  const handleBundleOptionAddToCart = (option, e) => {
    e?.stopPropagation();
    const productIds = (option.items || [])
      .map((id) => parseInt(String(id), 10))
      .filter((id) => !isNaN(id));
    setActiveBundleOption(option);
    setSelectedBundleProducts(productIds);
    addBundleToCartInternal(option, productIds);
  };

  const handleAddBundleToCart = () => {
    if (fixedBundleOptions.length > 0 && !activeBundleOption) {
      showError(b.errChoosePackage);
      return;
    }
    addBundleToCartInternal(activeBundleOption, selectedBundleProducts);
  };

  const handleBundleProductToggle = (rawProductId) => {
    const productId = Number(rawProductId);
    if (!Number.isFinite(productId)) return;

    if (fixedBundleOptions.length > 0) {
      showError(b.errUsePackages);
      return;
    }
    // Clear active option if manual toggle
    if (activeBundleOption) {
      setActiveBundleOption(null);
    }

    const bundleProduct = bundleProducts.find(p => Number(p.id) === productId);
    const isCurrentlySelected = selectedBundleProducts.some(id => Number(id) === productId);

    if (isCurrentlySelected) {
      // Deselecting - remove from selected and clear variant selections
      setSelectedBundleProducts(prev => prev.filter(id => Number(id) !== productId));
      setBundleVariantSelections(prev => {
        const updated = { ...prev };
        delete updated[productId];
        return updated;
      });
    } else {
      // Selecting - check max and initialize variant data if needed
      if (product.bundle_max_selections && selectedBundleProducts.length >= product.bundle_max_selections) {
        showError(fmt(b.errMax, { n: product.bundle_max_selections }));
        return;
      }

      setSelectedBundleProducts(prev => [...prev, productId]);

      // If product has variants, initialize variant selection state
      if (bundleProduct?.has_variants && bundleProduct?.variants_data) {
        try {
          const variantsData = typeof bundleProduct.variants_data === 'string'
            ? JSON.parse(bundleProduct.variants_data)
            : bundleProduct.variants_data;

          const sizes = variantsData.sizes || [];
          const colors = (variantsData.colors || []).map(c =>
            typeof c === 'string' ? { name: c, hex: c.toLowerCase() === 'black' ? '#000000' : c.toLowerCase() === 'white' ? '#ffffff' : '#808080' } : c
          );

          setBundleVariantSelections(prev => ({
            ...prev,
            [productId]: { size: '', color: '', sizes, colors }
          }));
        } catch (e) {
          console.error('Error parsing bundle product variants:', e);
        }
      }
    }
  };

  // Handle size selection for a bundle product
  const handleBundleVariantSize = (productId, size) => {
    setBundleVariantSelections(prev => ({
      ...prev,
      [productId]: { ...prev[productId], size }
    }));
  };

  // Handle color selection for a bundle product  
  const handleBundleVariantColor = (productId, color) => {
    setBundleVariantSelections(prev => ({
      ...prev,
      [productId]: { ...prev[productId], color }
    }));
  };

  const handleAddToCart = () => {
    if (isAdding) return;

    if (selectedQuantity > product.inventory) {
      showError(`Only ${product.inventory} items available in stock`);
      return;
    }

    // Validate variant selection
    if (hasVariants) {
      if (availableSizes.length > 0 && !selectedSize) {
        showError(b.errSize);
        return;
      }
      if (availableColors.length > 0 && !selectedColor) {
        showError(b.errColor);
        return;
      }
    }

    setIsAdding(true);

    try {
      const variantPriceAdjustmentCents = getVariantPriceAdjustmentCents(product, selectedSize, selectedColor);
      const itemToAdd = {
        ...product,
        quantity: selectedQuantity,
        unitPrice: wholesalePrice,
        price_cents: retailPrice,
        price: retailPrice,
        isWholesalePrice: isWholesalePricing,
        wholesaleTier: wholesaleTier,
        selectedSize: hasVariants ? selectedSize : null,
        selectedColor: hasVariants ? selectedColor : null,
        variantPriceAdjustmentCents,
        // Unique ID for cart item to distinguish variants
        cartItemId: hasVariants ? `${product.id}-${selectedSize || ''}-${selectedColor || ''}` : product.id
      };
      if (addItem(itemToAdd, selectedQuantity)) {
        const quantityText = selectedQuantity > 1 ? `${selectedQuantity} items` : '1 item';
        const priceText = isWholesalePricing
          ? ` at wholesale price: $${(wholesalePrice / 100).toFixed(2)} each`
          : '';

        showSuccess(`✓ Added ${quantityText} to cart${priceText}`);
      } else {
        showError('Could not add item to cart. Try refreshing the page.');
      }
    } catch (error) {
      showError('Failed to add item to cart. Please try again.');
    } finally {
      setTimeout(() => setIsAdding(false), 500);
    }
  };

  const handleAddRelatedProduct = async (e, relatedProduct) => {
    e.preventDefault(); // Prevent navigation
    e.stopPropagation();

    if (addingRelatedProduct === relatedProduct.id) return;

    setAddingRelatedProduct(relatedProduct.id);
    try {
      const relatedRetailPrice = relatedProduct.price_cents ?? relatedProduct.price;
      const relatedWholesalePrice = isWholesaler
        ? getWholesalePrice(relatedProduct, wholesaleTier, 1)
        : relatedRetailPrice;

      const itemToAdd = {
        ...relatedProduct,
        quantity: 1,
        unitPrice: relatedWholesalePrice,
        isWholesalePrice: relatedWholesalePrice !== relatedRetailPrice,
        wholesaleTier: wholesaleTier
      };

      if (addItem(itemToAdd, 1)) {
        showSuccess(`✓ Added ${relatedProduct.name} to cart`);
      } else {
        showError('Could not add item to cart.');
      }
    } catch (error) {
      showError('Failed to add item to cart. Please try again.');
    } finally {
      setTimeout(() => setAddingRelatedProduct(null), 500);
    }
  };

  if (loading) return <LoadingSpinner size="medium" message={p.loading} />;
  if (!product) return <div className="product-detail-message">{p.notFound}</div>;

  const isOutOfStock = product.inventory === 0;

  /** Max cards in "Sample Tunes of this Product" (admin: product definition). */
  const sampleTunesLimit = Math.min(
    99,
    Math.max(1, parseInt(product.sample_tunes_limit, 10) || 9)
  );
  const displayedSampleTunes =
    relatedTunes.length > sampleTunesLimit
      ? relatedTunes.slice(0, sampleTunesLimit)
      : relatedTunes;
  const hasMoreSampleTunesThanShown = relatedTunes.length > sampleTunesLimit;

  // Calculate pricing based on user type and quantity
  const variantPriceAdjustmentCents = getVariantPriceAdjustmentCents(product, selectedSize, selectedColor);
  const retailPrice = getVariantAdjustedPriceCents(product, selectedSize, selectedColor);
  const baseWholesalePrice = isWholesaler ? getWholesalePrice(product, wholesaleTier, selectedQuantity) : retailPrice;
  const wholesalePrice = isWholesaler ? baseWholesalePrice + variantPriceAdjustmentCents : retailPrice;
  const isWholesalePricing = wholesalePrice !== retailPrice;
  const savings = retailPrice - wholesalePrice;
  const savingsPercent = retailPrice > 0 ? Math.round((savings / retailPrice) * 100) : 0;
  const selectedVariantLabel = [selectedSize, selectedColor].filter(Boolean).join(' / ');

  // Get available wholesale tiers for current quantity
  const availableTiers = isWholesaler ? getAvailableWholesaleTiers(product, selectedQuantity) : [];

  return (
    <>
      <SEO
        title={`${product.name} | Cranford Publications`}
        description={product.description?.substring(0, 160) || `${product.name} - ${product.category || 'Celtic Music'} from Cranford Publications`}
        canonical={`/product/${product.permalink || product.id}`}
        image={product.image || 'https://www.cranfordpub.ca/images/collage9.jpg'}
        type="product"
        keywords={`${product.name}, ${product.category}, Celtic music, fiddle music, ${product.sku}`}
      />
      <div className="product-detail-container">
        {/* Edit button for admin users */}
        {canEdit && product && (
          <button
            className="edit-product-btn"
            onClick={() => navigate(`/admin/products?edit=${product.id}`)}
            title="Edit this product"
          >
            {getIcon('edit')} Edit Product
          </button>
        )}

        <ShareFloatingButton
          title={product.name}
          url={`${window.location.origin}/product/${product.permalink || product.id}`}
          text={product.name}
          openEmailModal={() => setShowShareModal(true)}
          showSuccess={showSuccess}
          titleAttr="Share this product"
          ariaLabel="Share this product"
        />

        <div className="product-detail-content">
          <div className="page-nav-bar">
            <span />
            <button onClick={() => navigate(-1)} className="back-button" title="Back">←</button>
          </div>

          {/* Bundle Product Layout - Image left, info right */}
          {(product.is_bundle === 1 || product.is_bundle === true) ? (
            fixedBundleOptions.length > 0 ? (
              <div className="bundle-compact">
                <div className="bundle-compact-header">
                  <div className="bundle-compact-image">
                    <AsyncProductImage
                      product={product}
                      alt={product.name}
                      loading="eager"
                      fetchPriority="high"
                    />
                  </div>
                  <div className="bundle-compact-info">
                    <h2 className="bundle-title">{product.name}</h2>
                    <p className="product-artist">
                      {(product.category?.toLowerCase().includes('book') || product.type === 'book')
                        ? product.author
                        : product.artist}
                    </p>
                    {canEdit && <ProductOriginalPageLinks sourceUrl={product.source_url} />}
                    <div
                      className="bundle-compact-desc"
                      dangerouslySetInnerHTML={{ __html: product.description }}
                    />
                    <p className="bundle-compact-from">
                      {b.packagesFrom}{' '}
                      <strong>
                        ${Math.min(...fixedBundleOptions.map((o) => parseFloat(o.price) || 0)).toFixed(2)}
                      </strong>
                    </p>
                  </div>
                </div>

                <div className="bundle-package-list">
                  <h3 className="bundle-package-list-heading">{p.choosePackage}</h3>
                  <p className="bundle-package-list-sub">
                    {b.packageSub}
                  </p>
                  {fixedBundleOptions.map((option, idx) => {
                    const included = getOptionIncludedProducts(option);
                    const isActive = activeBundleOption === option;
                    return (
                      <div
                        key={idx}
                        className={`bundle-package-card ${isActive ? 'is-active' : ''}`}
                      >
                        <div className="bundle-package-thumbs" aria-hidden="true">
                          {included.map((p) => (
                            <div key={p.id} className="bundle-package-thumb" title={p.name}>
                              <div className="bundle-package-thumb-img">
                                <AsyncProductImage product={p} loading="lazy" />
                              </div>
                              <span className="bundle-package-thumb-label">
                                {bundleProductShortLabel(p)}
                              </span>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          className="bundle-package-select"
                          onClick={() => handleBundleOptionSelect(option)}
                          aria-pressed={isActive}
                          aria-label={`${option.name}: ${included.map((p) => p.name).join(', ')}`}
                        >
                          <span className="bundle-package-name">{option.name}</span>
                        </button>
                        <div className="bundle-package-actions">
                          <span className="bundle-package-price">
                            ${parseFloat(option.price).toFixed(2)}
                          </span>
                          <button
                            type="button"
                            className={`bundle-package-add-btn ${isAdding && isActive ? 'is-adding' : ''}`}
                            onClick={(e) => handleBundleOptionAddToCart(option, e)}
                            disabled={isAdding || isOutOfStock}
                          >
                            {isAdding && isActive ? b.adding : b.addToCart}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="bundle-compact">
                <div className="bundle-compact-header">
                  <div className="bundle-compact-image">
                    <AsyncProductImage
                      product={product}
                      alt={product.name}
                      loading="eager"
                      fetchPriority="high"
                    />
                  </div>
                  <div className="bundle-compact-info">
                    <h2 className="bundle-title">{product.name}</h2>
                    <p className="product-artist">
                      {(product.category?.toLowerCase().includes('book') || product.type === 'book')
                        ? product.author
                        : product.artist}
                    </p>
                    {canEdit && <ProductOriginalPageLinks sourceUrl={product.source_url} />}
                    <div
                      className="bundle-compact-desc"
                      dangerouslySetInnerHTML={{ __html: product.description }}
                    />
                  </div>
                </div>

                {bundleProducts.length > 0 ? (
                  <div className="bundle-pick-list">
                    <div className="bundle-pick-toolbar">
                      <h3 className="bundle-pick-heading">{b.buildHeading}</h3>
                      <span className="bundle-pick-count">
                        {product.bundle_max_selections
                          ? fmt(b.selectedMax, { n: selectedBundleProducts.length, max: product.bundle_max_selections })
                          : fmt(b.selectedCount, { n: selectedBundleProducts.length })}
                      </span>
                    </div>

                    {(() => {
                      const status = getVariableBundleSelectionStatus();
                      const min = product.bundle_min_quantity;
                      return (
                        <div className={`bundle-pick-status bundle-pick-status--${status.type}`}>
                          <p className="bundle-pick-status-message">{status.message}</p>
                          {min > 0 && product.bundle_discount > 0 && status.type !== 'success' && (
                            <div className="bundle-pick-progress-track" aria-hidden="true">
                              <div
                                className="bundle-pick-progress-fill"
                                style={{ width: `${status.progressPct}%` }}
                              />
                            </div>
                          )}
                          {min > 0 && product.bundle_discount > 0 && (
                            <p className="bundle-pick-status-meta">
                              {fmt(b.towardDiscount, {
                                selected: selectedBundleProducts.length,
                                min,
                                pct: product.bundle_discount,
                              })}
                            </p>
                          )}
                        </div>
                      );
                    })()}

                    <div className="bundle-pick-rows">
                      {[...bundleProducts]
                        .sort((a, b) => {
                          const aOos = !a.inventory || a.inventory === 0;
                          const bOos = !b.inventory || b.inventory === 0;
                          if (aOos === bOos) return 0;
                          return aOos ? 1 : -1;
                        })
                        .map((bundleProduct) => {
                          const isSelected = selectedBundleProducts.some((id) => Number(id) === Number(bundleProduct.id));
                          const isProductOos = !bundleProduct.inventory || bundleProduct.inventory === 0;
                          const { adjustedPriceCents, adjustmentCents } = getBundleProductPricing(bundleProduct);
                          return (
                            <div
                              key={bundleProduct.id}
                              className={`bundle-pick-row ${isSelected ? 'is-selected' : ''} ${isProductOos ? 'is-disabled' : ''}`}
                              onClick={() => !isProductOos && handleBundleProductToggle(bundleProduct.id)}
                              onKeyDown={(e) => {
                                if (!isProductOos && (e.key === 'Enter' || e.key === ' ')) {
                                  e.preventDefault();
                                  handleBundleProductToggle(bundleProduct.id);
                                }
                              }}
                              role="button"
                              tabIndex={isProductOos ? -1 : 0}
                              aria-pressed={isSelected}
                              aria-disabled={isProductOos}
                            >
                              <div className="bundle-pick-row-thumb">
                                <AsyncProductImage product={bundleProduct} loading="lazy" />
                              </div>
                              <div className="bundle-pick-row-info">
                                <div className="bundle-pick-row-name">{bundleProduct.name}</div>
                                <div className="bundle-pick-row-meta">
                                  {bundleProduct.author || bundleProduct.artist || b.unknownArtist}
                                  {isProductOos && <span className="bundle-pick-row-oos"> · {b.oos}</span>}
                                </div>
                                {isSelected && bundleVariantSelections[bundleProduct.id] && (
                                  <div className="bundle-pick-row-variants" onClick={(e) => e.stopPropagation()}>
                                    {bundleVariantSelections[bundleProduct.id].sizes?.length > 0 && (
                                      <>
                                        {!bundleVariantSelections[bundleProduct.id].size && (
                                          <div className="bundle-pick-variant-hint is-pending">
                                            {b.chooseSize}
                                          </div>
                                        )}
                                        <div className="bundle-pick-variant-group">
                                          {bundleVariantSelections[bundleProduct.id].sizes.map((size) => (
                                            <button
                                              key={size}
                                              type="button"
                                              className={`bundle-pick-variant-btn ${bundleVariantSelections[bundleProduct.id].size === size ? 'is-active' : ''}`}
                                              onClick={() => handleBundleVariantSize(bundleProduct.id, size)}
                                            >
                                              {size}
                                            </button>
                                          ))}
                                        </div>
                                      </>
                                    )}
                                    {bundleVariantSelections[bundleProduct.id].colors?.length > 0 && (
                                      <>
                                        <div className={`bundle-pick-variant-hint ${bundleVariantSelections[bundleProduct.id].color ? 'is-complete' : 'is-pending'}`}>
                                          {bundleVariantSelections[bundleProduct.id].colors.map((color) => color.name).join(', ')} {'<<<'}                                           {bundleVariantSelections[bundleProduct.id].color
                                            ? fmt(b.selectedColor, { color: bundleVariantSelections[bundleProduct.id].color })
                                            : b.chooseColor}
                                        </div>
                                        <div className="bundle-pick-variant-group">
                                          {bundleVariantSelections[bundleProduct.id].colors.map((color) => (
                                            <button
                                              key={color.name}
                                              type="button"
                                              className={`bundle-pick-color-btn ${bundleVariantSelections[bundleProduct.id].color === color.name ? 'is-active' : ''}`}
                                              style={{ background: color.hex }}
                                              title={color.name}
                                              onClick={() => handleBundleVariantColor(bundleProduct.id, color.name)}
                                            />
                                          ))}
                                        </div>
                                      </>
                                    )}
                                    {adjustmentCents !== 0 && (
                                      <div
                                        style={{
                                          marginTop: '4px',
                                          fontSize: '0.82rem',
                                          color: 'var(--color-text-secondary)'
                                        }}
                                      >
                                        {b.variantAdjustment} <strong>{formatAdjustmentLabel(adjustmentCents)}</strong>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="bundle-pick-row-price">
                                ${((adjustedPriceCents || 0) / 100).toFixed(2)}
                                {adjustmentCents !== 0 && (
                                  <div
                                    style={{
                                      fontSize: '0.78rem',
                                      color: 'var(--color-text-secondary)',
                                      marginTop: '2px'
                                    }}
                                  >
                                    {formatAdjustmentLabel(adjustmentCents)}
                                  </div>
                                )}
                              </div>
                              <input
                                type="checkbox"
                                className="bundle-pick-row-check"
                                checked={isSelected}
                                disabled={isProductOos}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => {
                                  if (!isProductOos) {
                                    handleBundleProductToggle(bundleProduct.id);
                                  }
                                }}
                                tabIndex={-1}
                                aria-label={fmt(isSelected ? b.deselectAria : b.selectAria, { name: bundleProduct.name })}
                              />
                            </div>
                          );
                        })}
                    </div>

                    {(() => {
                      const {
                        subtotalCents,
                        discountAmountCents,
                        finalTotalCents,
                        qualifies,
                        neededForDiscount,
                        discountPct,
                      } = getVariableBundlePricing();
                      const canAdd = selectedBundleProducts.length > 0
                        && (!product.bundle_min_quantity || selectedBundleProducts.length >= product.bundle_min_quantity);

                      return (
                        <div className="bundle-pick-footer">
                          {selectedBundleProducts.length > 0 ? (
                            <div className="bundle-pick-totals">
                              <div className="bundle-pick-total-line">
                                <span>{fmt(b.subtotalCount, { n: selectedBundleProducts.length })}</span>
                                <span>${(subtotalCents / 100).toFixed(2)}</span>
                              </div>
                              {qualifies && (
                                <>
                                  <div className="bundle-pick-total-line bundle-pick-total-line--discount">
                                    <span>{fmt(b.discountLine, { pct: discountPct })}</span>
                                    <span>-${(discountAmountCents / 100).toFixed(2)}</span>
                                  </div>
                                  <div className="bundle-pick-total-line bundle-pick-total-line--final">
                                    <span>{b.total}</span>
                                    <strong>${(finalTotalCents / 100).toFixed(2)}</strong>
                                  </div>
                                </>
                              )}
                              {neededForDiscount > 0 && (
                                <p className="bundle-pick-nudge bundle-pick-nudge--warning">
                                  {fmt(neededForDiscount === 1 ? b.pickMoreOne : b.pickMoreMany, {
                                    n: neededForDiscount,
                                    pct: discountPct,
                                  })}
                                </p>
                              )}
                            </div>
                          ) : (
                            <p className="bundle-pick-nudge">
                              {product.bundle_discount
                                ? fmt(b.pickMinToUnlock, {
                                    min: product.bundle_min_quantity || 1,
                                    pct: product.bundle_discount,
                                  })
                                : fmt(
                                    (product.bundle_min_quantity || 1) === 1 ? b.pickMinOne : b.pickMinMany,
                                    { min: product.bundle_min_quantity || 1 }
                                  )}
                              {product.bundle_max_selections
                                ? fmt(b.upToMax, { max: product.bundle_max_selections })
                                : ''}
                            </p>
                          )}
                          <button
                            type="button"
                            className={`bundle-pick-add-btn ${isAdding ? 'is-adding' : ''}`}
                            onClick={handleAddBundleToCart}
                            disabled={isOutOfStock || isAdding || !canAdd}
                          >
                            {isAdding
                              ? b.adding
                              : selectedBundleProducts.length
                                ? fmt(b.addBundleCount, { n: selectedBundleProducts.length })
                                : b.addBundle}
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="bundle-pick-loading">
                    <p>{b.loadingProducts}</p>
                  </div>
                )}
              </div>
            )
          ) : (
            // Regular Product Layout
            <>
              {/* Left column: Button + Image */}
              <div className="product-image-column">
                <AsyncProductImage
                  product={product}
                  alt={product.name}
                  className="product-detail-image"
                  loading="eager"
                  fetchPriority="high"
                />
              </div>

              {/* Right column: Product Details */}
              <div className="product-detail-info">
                <h2>{product.name}</h2>
                {(product.is_digital_album === 1 || product.is_digital_album === true) && (
                  <p className="digital-album-badge">
                    📥 Digital download — ZIP with licensed MP3s after purchase
                  </p>
                )}
                <p className="product-artist">{(product.category?.toLowerCase().includes('book') || product.type === 'book') ? product.author : product.artist}</p>

                {canEdit && <ProductOriginalPageLinks sourceUrl={product.source_url} />}

                {/* Review Link */}
                {reviewPageExists && reviewPageId && (
                  <div style={{ marginBottom: '1rem' }}>
                    <Link
                      to={`/page/${reviewPageId}`}
                      className="btn btn-outline-primary"
                      style={{
                        display: 'inline-block',
                        padding: '0.5rem 1rem',
                        fontSize: '0.9rem',
                        textDecoration: 'none',
                        border: '1px solid #238636',
                        borderRadius: '6px',
                        color: '#238636',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#238636';
                        e.target.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'transparent';
                        e.target.style.color = '#238636';
                      }}
                    >
                      📝 Read Review
                    </Link>
                  </div>
                )}

                <div
                  className="product-description"
                  dangerouslySetInnerHTML={{ __html: product.description }}
                />

                {/* Pricing Section - Only for non-bundle products */}
                <div className="product-pricing">
                  {isWholesaler && product.wholesalePricing ? (
                    <div className="wholesale-pricing">
                      <div className="price-display">
                        {isWholesalePricing ? (
                          <>
                            <div className="wholesale-price">
                              ${(wholesalePrice / 100).toFixed(2)}
                              <span className="wholesale-tier-badge">{wholesaleTier}</span>
                            </div>
                            <div className="retail-price-crossed">
                              ${(retailPrice / 100).toFixed(2)}
                            </div>
                            <div className="savings-badge">
                              Save {savingsPercent}%
                            </div>
                          </>
                        ) : (
                          <div className="retail-price">
                            ${(retailPrice / 100).toFixed(2)}
                            <span className="no-wholesale-text">Min qty for wholesale: {product.wholesalePricing[wholesaleTier]?.minQuantity || 5}</span>
                          </div>
                        )}
                        {selectedVariantLabel && (
                          <div
                            style={{
                              marginTop: '6px',
                              fontSize: '0.9rem',
                              color: 'var(--color-text-secondary)'
                            }}
                          >
                            Variant: <strong>{selectedVariantLabel}</strong>
                            {variantPriceAdjustmentCents !== 0 && (
                              <> ({formatAdjustmentLabel(variantPriceAdjustmentCents)})</>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Quantity Selector for Wholesalers */}
                      <div className="quantity-selector">
                        <label htmlFor={`quantity-${product.id}`}>Quantity:</label>
                        <select
                          id={`quantity-${product.id}`}
                          value={selectedQuantity}
                          onChange={handleQuantityChange}
                          className="quantity-select"
                        >
                          {[...Array(Math.min(product.inventory, 50))].map((_, i) => (
                            <option key={i + 1} value={i + 1}>{i + 1}</option>
                          ))}
                        </select>
                      </div>

                      {/* Wholesale Tier Information */}
                      {availableTiers.length > 0 && (
                        <div className="tier-info">
                          <small>Available discounts at this quantity:</small>
                          <div className="tier-badges">
                            {availableTiers.map((tier) => (
                              <span
                                key={tier.tier}
                                className={`tier-badge ${tier.tier === wholesaleTier ? 'active' : ''}`}
                              >
                                {tier.tier}: {tier.savingsPercent}% off
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="retail-pricing">
                      <div className="product-price">${(retailPrice / 100).toFixed(2)}</div>
                      {selectedVariantLabel && (
                        <div
                          style={{
                            marginTop: '6px',
                            fontSize: '0.9rem',
                            color: 'var(--color-text-secondary)'
                          }}
                        >
                          Variant: <strong>{selectedVariantLabel}</strong>
                          {variantPriceAdjustmentCents !== 0 && (
                            <> ({formatAdjustmentLabel(variantPriceAdjustmentCents)})</>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Variant Selectors */}
                {hasVariants && (
                  <div className="product-variants" style={{ marginBottom: '15px', padding: '12px', background: 'var(--color-surface, #f8f9fa)', borderRadius: '8px', border: '1px solid var(--color-border, #e9ecef)' }}>
                    {availableSizes.length > 0 && (
                      <div className="variant-selector size-selector" style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.9rem', color: 'var(--color-text-primary, #333)' }}>Size:</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {availableSizes.map(size => (
                            <button
                              key={size}
                              className={`variant-btn ${selectedSize === size ? 'selected' : ''}`}
                              onClick={() => setSelectedSize(size)}
                              style={{
                                padding: '6px 12px',
                                fontSize: '0.9rem',
                                border: selectedSize === size ? '1px solid var(--color-primary, #4a90e2)' : '1px solid var(--color-border, #ddd)',
                                borderRadius: '4px',
                                background: selectedSize === size ? 'var(--color-primary, #4a90e2)' : 'var(--color-surface, #fff)',
                                color: selectedSize === size ? '#fff' : 'var(--color-text-primary, #333)',
                                cursor: 'pointer',
                                fontWeight: selectedSize === size ? '600' : 'normal',
                                transition: 'all 0.2s',
                                minWidth: '40px'
                              }}
                            >
                              {size}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {availableColors.length > 0 && (
                      <div className="variant-selector color-selector">
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.9rem', color: 'var(--color-text-primary)' }}>Color:</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {availableColors.map(color => (
                            <button
                              key={color.name}
                              className={`variant-btn ${selectedColor === color.name ? 'selected' : ''}`}
                              onClick={() => setSelectedColor(color.name)}
                              title={color.name}
                              style={{
                                width: '30px',
                                height: '30px',
                                borderRadius: '50%',
                                border: selectedColor === color.name ? '2px solid var(--color-primary, #4a90e2)' : '1px solid var(--color-border, #ddd)',
                                background: color.hex,
                                cursor: 'pointer',
                                boxShadow: selectedColor === color.name ? '0 0 0 2px var(--color-surface, #fff), 0 0 0 4px var(--color-primary, #4a90e2)' : '0 1px 3px rgba(0,0,0,0.1)',
                                position: 'relative',
                                transition: 'transform 0.2s',
                                margin: selectedColor === color.name ? '2px' : '0' // Add margin to prevent crop when selected
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                              {selectedColor === color.name && (
                                <span style={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  color: ['#FFFFFF', '#fff', '#ffffff', 'white'].includes(color.hex.toLowerCase()) ? '#000' : '#fff',
                                  fontWeight: 'bold',
                                  fontSize: '12px',
                                  textShadow: '0 0 2px rgba(0,0,0,0.5)'
                                }}>✓</span>
                              )}
                            </button>
                          ))}
                        </div>
                        {selectedColor && <div style={{ marginTop: '4px', fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>Selected: <strong>{selectedColor}</strong></div>}
                      </div>
                    )}
                    {variantPriceAdjustmentCents !== 0 && (
                      <div style={{ marginTop: '10px', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
                        Variant price adjustment:{' '}
                        <strong>
                          {variantPriceAdjustmentCents > 0 ? '+' : '-'}${(Math.abs(variantPriceAdjustmentCents) / 100).toFixed(2)}
                        </strong>
                      </div>
                    )}
                  </div>
                )}

                <button
                  className={`add-to-cart-btn ${isAdding ? 'adding' : ''}`}
                  onClick={(product.is_bundle === 1 || product.is_bundle === true) ? handleAddBundleToCart : handleAddToCart}
                  disabled={
                    isOutOfStock ||
                    isAdding ||
                    ((product.is_bundle === 1 || product.is_bundle === true) && selectedBundleProducts.length === 0)
                  }
                  style={{
                    // Opacity reduced only if out of stock or adding
                    opacity: (isOutOfStock || isAdding) ? 0.8 : 1,
                    cursor: (isOutOfStock || isAdding) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isOutOfStock ? (
                    <>
                      {getIcon('error')} {p.outOfStock}
                    </>
                  ) : isAdding ? (
                    <>
                      {getIcon('success')} {p.adding}
                    </>
                  ) : (product.is_bundle === 1 || product.is_bundle === true) ? (
                    <>
                      {getIcon('bundle')} {fmt(b.addBundleCount, { n: selectedBundleProducts.length })}
                    </>
                  ) : (hasVariants && ((availableSizes.length > 0 && !selectedSize) || (availableColors.length > 0 && !selectedColor))) ? (
                    <>
                      Select Options
                    </>
                  ) : isWholesaler ? (
                    <>
                      {getIcon('add')} Add {selectedQuantity} to Cart
                    </>
                  ) : (
                    <>
                      {getIcon('add')} {p.addToCart}
                    </>
                  )}
                </button>

                {isOutOfStock && digitalAlternative && !(product.is_digital_album === 1 || product.is_digital_album === true) && (
                  <div className="digital-fallback-banner">
                    <p className="digital-fallback-lead">
                      📥 Physical CD out of stock — digital download available
                    </p>
                    <p className="digital-fallback-detail">
                      {digitalAlternative.name} — ${((digitalAlternative.price_cents || 0) / 100).toFixed(2)} CAD
                      {' '}(instant licensed ZIP after purchase)
                    </p>
                    <button
                      type="button"
                      className="digital-fallback-btn"
                      onClick={() => navigate(`/product/${digitalAlternative.permalink || digitalAlternative.id}`)}
                    >
                      View digital version
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Index Page Link - show if index exists, even without sample tunes */}
        {(product?.category?.toLowerCase().includes('book') || product?.item_type?.toLowerCase() === 'book') &&
          indexPageExists && indexPageId && !tunesLoading && relatedTunes.length === 0 && (
            <div className="related-tunes-section">
              <div className="tunes-section-header">
                <h3>Book Index</h3>
                <Link
                  to={`/page/${indexPageId}`}
                  className="btn btn-outline-primary view-index-btn"
                >
                  📖 View Complete Index
                </Link>
              </div>
              <p>This book has a complete index available. Click above to view all tunes.</p>
            </div>
          )}

        {/* Related Tunes Section - for books and CDs - only show if there are tunes or loading */}
        {((product?.category?.toLowerCase().includes('book') || product?.item_type?.toLowerCase() === 'book') ||
          (product?.category?.toLowerCase().includes('cd') || product?.item_type?.toLowerCase() === 'cd') ||
          product?.is_digital_album === 1 || product?.is_digital_album === true ||
          id === '686') &&
          (tunesLoading || relatedTunes.length > 0) && (
            <div className="related-tunes-section">
              <div className="tunes-section-header">
                <h3>Sample Tunes of this Product</h3>
                {indexPageExists && indexPageId && (
                  <Link
                    to={`/page/${indexPageId}`}
                    className="btn btn-outline-primary view-index-btn"
                  >
                    📖 View Complete Index
                  </Link>
                )}
              </div>
              {tunesLoading ? (
                <p>Loading tunes...</p>
              ) : (
                <>
                  <div className="tunes-grid">
                    {displayedSampleTunes.map(tune => (
                      <Link
                        key={tune.pageId || tune.assetId || tune.title}
                        to={tune.url || (tune.assetId ? `/tune-asset/${tune.assetId}` : `/page/${tune.pageId}`)}
                        className="tune-card tune-card--marked"
                      >
                        <div className="tune-card-body">
                          <div className="tune-card-title-row">
                            <h4>{tune.title}</h4>
                            <TuneFormatMarker item={tune} variant="inline" />
                          </div>
                          {tune.artist && <p className="tune-artist">🎻 {tune.artist}</p>}
                        </div>
                        <div className="tune-card-footer">
                          {tune.tuneType && (
                            <div className="tune-footer-item">
                              <span className="footer-label">Type:</span>
                              <span className="footer-value">{tune.tuneType}</span>
                            </div>
                          )}
                          {tune.region && (
                            <div className="tune-footer-item">
                              <span className="footer-label">Region:</span>
                              <span className="footer-value">{tune.region}</span>
                            </div>
                          )}
                          {product?.name && (
                            <div className="tune-footer-item">
                              <span className="footer-label">Book:</span>
                              <span className="footer-value">{product.name}</span>
                            </div>
                          )}
                          {tune.bookPage && (
                            <div className="tune-footer-item">
                              <span className="footer-label">Page:</span>
                              <span className="footer-value tune-page-number">{tune.bookPage}</span>
                            </div>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                  {indexPageExists && indexPageId && hasMoreSampleTunesThanShown && (
                    <div className="view-all-tunes">
                      <Link
                        to={`/page/${indexPageId}`}
                        className="btn btn-secondary"
                      >
                        View all {relatedTunes.length} tunes
                      </Link>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

        {/* Related Products Section */}
        {relatedProducts.length > 0 && (
          <div className="related-products-section">
            <div className="section-header">
              <h3>🎯 You May Also Be Interested In</h3>
            </div>
            {relatedProductsLoading ? (
              <p>Loading related products...</p>
            ) : (
              <div className="related-products-grid">
                {relatedProducts.map(relatedProduct => (
                  <div
                    key={relatedProduct.id}
                    className="related-product-card"
                  >
                    <Link
                      to={`/product/${relatedProduct.id}`}
                      className="related-product-link"
                      title={relatedProduct.name}
                    >
                      <div className="related-product-image">
                        <AsyncProductImage
                          product={relatedProduct}
                          alt={relatedProduct.name}
                          loading="lazy"
                          className="product-image"
                        />
                      </div>
                      <span className="related-product-price">
                        ${((relatedProduct.price_cents ?? relatedProduct.price) / 100).toFixed(2)}
                      </span>
                    </Link>
                    <button
                      className="related-product-add-btn"
                      onClick={(e) => handleAddRelatedProduct(e, relatedProduct)}
                      disabled={addingRelatedProduct === relatedProduct.id || relatedProduct.inventory === 0}
                    >
                      {addingRelatedProduct === relatedProduct.id ? `✓ ${p.adding}` : relatedProduct.inventory === 0 ? p.outOfStock : `🛒 ${p.addToCart}`}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {product && (
          <UserContentFeedback
            targetType="product"
            targetId={String(product.id)}
            settingKey="user_ratings_products_enabled"
            title="Ratings & reviews"
          />
        )}

        {/* Share Modal */}
        {product && (
          <ShareModal
            isOpen={showShareModal}
            onClose={() => setShowShareModal(false)}
            contentTitle={product.name}
            contentUrl={`${window.location.origin}/product/${product.id}`}
            contentId={product.id.toString()}
            contentType="product"
            contentDescription={`${product.category || 'Product'} by ${product.artist || product.author || 'Cranford Publications'} - $${(product.price_cents / 100).toFixed(2)}`}
          />
        )}
      </div >
    </>
  );
};

export default ProductDetail; 